import { Sentry } from './sentry';

/**
 * Privacy-safe product analytics via existing Sentry infrastructure.
 * Never include document text, PII, names, addresses, IDs, or educational-record content.
 */

export type AccountSegment = 'pilot' | 'paid' | 'free' | 'unknown';

export type PrepareAnalyticsProps = {
  accountSegment?: AccountSegment;
  pageCount?: number;
  durationMs?: number;
  documentCategory?: string;
  success?: boolean;
  failureReason?: 'integrity' | 'processing' | 'validation' | 'unknown';
  fileExtension?: string;
};

function safeCategory(value: string | undefined): string | undefined {
  if (!value || value.trim().length === 0) return undefined;
  // Allow only known short category ids — never free-form user text that could contain PII
  const allowed = new Set([
    'iep',
    'evaluation_fie',
    'progress_report',
    'behavior_intervention',
    'service_documentation',
    'instructional_material',
    'pd_training',
    'other',
  ]);
  return allowed.has(value) ? value : 'other';
}

function safeExtension(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const ext = value.replace(/^\./, '').toLowerCase().slice(0, 8);
  if (!/^[a-z0-9]+$/.test(ext)) return undefined;
  return ext;
}

export function trackPrepareEvent(
  event:
    | 'document_prepare_started'
    | 'document_prepare_succeeded'
    | 'document_prepare_failed'
    | 'document_prepare_downloaded',
  props: PrepareAnalyticsProps = {}
): void {
  try {
    Sentry.captureMessage(event, {
      level: props.success === false ? 'warning' : 'info',
      tags: {
        product_event: event,
        account_segment: props.accountSegment ?? 'unknown',
        document_category: safeCategory(props.documentCategory) ?? 'unspecified',
        failure_reason: props.failureReason ?? 'none',
        file_extension: safeExtension(props.fileExtension) ?? 'unknown',
      },
      extra: {
        page_count: props.pageCount,
        duration_ms: props.durationMs,
        success: props.success,
      },
    });
  } catch {
    // Analytics must never break the product flow
  }
}

export function accountSegmentFromTier(
  tier: string,
  isActive: boolean
): AccountSegment {
  if (!isActive || tier === 'free') return 'free';
  if (tier === 'educator_pilot') return 'pilot';
  return 'paid';
}
